import type { DeclarationFrameworkSlice } from "../../../../api/declarationsApi"
import type { ArticleState } from "../../../../api/types"
import type { FlowInputs } from "./flowState"

/**
 * STEP C3-b2: ONE REGULATION'S SLICE AS THE DERIVATION'S INPUTS.
 *
 * `deriveOpenWork` stays untouched: the slice is reshaped into exactly what the four
 * full reads hand it today, cut to one regulation. `/frameworks/{fw}` answers each
 * part in today's shape (C3-a, pinned by its parity tests against the full reads):
 * the regulation's statement group, the read model cut to it, its condition rows plus
 * the framework-less ones.
 *
 * ARTICLE STATES. The derivation reads two things off `/article-states`: the supplier
 * pool's numbers in pool order (`poolNumbers`) and each article's on-record verdict
 * for a framework (`frameworkStatuses`). The slice carries both for its regulation —
 * `poolNumbers` (the supplier pool, never the candidate fallback, the same list
 * `/article-states` walks) and `previousVerdicts` (the last-written status per
 * article, the same last-wins the full read's loop applies). So each pool article
 * becomes one state with at most one compliance entry naming this framework.
 */
export function articleStatesFromSlice(
  slice: DeclarationFrameworkSlice,
): ArticleState[] {
  return slice.poolNumbers.map((articleNumber) => {
    const has = Object.prototype.hasOwnProperty.call(
      slice.previousVerdicts,
      articleNumber,
    )
    return {
      articleNumber,
      name: "",
      substances: [],
      compliance: has
        ? [
            {
              regulationName: null,
              status: "",
              source: "",
              frameworkStatuses: {
                [slice.framework]: slice.previousVerdicts[
                  articleNumber
                ] as string,
              },
            },
          ]
        : [],
    }
  })
}

/** The four inputs `deriveOpenWork` takes, from one regulation's slice. */
export function flowInputsFromSlice(
  slice: DeclarationFrameworkSlice,
): Pick<
  FlowInputs,
  "statements" | "readModel" | "articleStates" | "conditionRows"
> {
  return {
    statements: [slice.statements],
    readModel: slice.readModel,
    articleStates: articleStatesFromSlice(slice),
    conditionRows: slice.conditionRows,
  }
}
